import cx from 'classnames';
import uuid from 'uuid';
import Modal from 'react-modal';
import React, { useState, useEffect } from 'react';
import { DocumentNode } from 'graphql';
import { isAfter, isSameDay, addDays, addWeeks } from 'date-fns';

import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Checkbox from 'components/Checkbox';

import CoachingConstants from 'coaching/CoachingConstants';
import { CoachPauseReasonsSelect } from 'coaching/components/CoachAvailabilityView/CoachPauseReasonsSelect';
import ConfirmDialog from 'components/ConfirmDialog';
import Container from 'components/Container';
import DatePickerInput from 'components/DatePickerInput';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Label from 'components/Label';
import Row from 'components/Row';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import ToastActions from 'actions/ToastActions';

import assertions from 'lib/assertions';
import { dateOnlyFormat } from 'lib/dateTimeFormat';
import { userReadableDateFormat } from 'lib/dateTimeFormat';

import { CoachPause } from 'coaching/containers/CoachAvailabilityContainer';
import { saveCoachPause, cancelCoachPause } from 'coaching/actions/CoachingActions';
import { PauseReason } from 'globalTypes';
import EventTracking from 'lib/EventTracking';

import '../styles/PauseTutoringView.scss';

const eventPrefix = 'coach_availability.pause_coaching';

function dateOrUndefined(x: string | number | undefined) {
  return x ? new Date(x) : undefined;
}

function trackKey(key: string) {
  EventTracking.trackKey(`${eventPrefix}.${key}`);
}

/** Returns the start of tomorrow in local time. */
function calcMinEffectiveDate() {
  const tomorrow = addDays(new Date(), 1);
  return new Date(tomorrow.setHours(0, 0, 0, 0));
}

export type Props = {
  onBack: () => void;
  onSubmit?: () => void;
  pause?: CoachPause;
  availabilityQuery?: { query: DocumentNode; variables?: Object };
};

type ConfirmationProps = {
  onConfirm: () => void;
  onClose: () => void;
};

function SetPauseConfirmation({
  onConfirm,
  onClose,
  userReadableEffectiveDate,
}: ConfirmationProps & { userReadableEffectiveDate: string }) {
  useEffect(() => {
    trackKey('confirm_pause_modal.view');
  }, []);
  return (
    <ConfirmDialog
      actionButtonText="Continue to set pause"
      cancelButtonText="Cancel"
      cancelButtonColor="mascot"
      cancelButtonLevel="icon"
      size="small"
      onConfirm={() => {
        trackKey('confirm_pause_modal.continue.click');
        onConfirm();
      }}
      onCancel={() => {
        trackKey('confirm_pause_modal.cancel.click');
        onClose();
      }}
      onRequestHide={() => {
        trackKey('confirm_pause_modal.close.click');
        onClose();
      }}>
      <Heading serif>Your students will be rematched</Heading>
      <Text align="left" color="graphite" element="p">
        Once you set your pause, we may begin rematching your students to a different tutor.
      </Text>
      <Text align="left" color="graphite" element="p">
        Starting {userReadableEffectiveDate}, all of your sessions going forward will be cancelled.
        You should continue sessions up until the pause starts.
      </Text>
    </ConfirmDialog>
  );
}

function CancelPauseConfirmation({ onConfirm, onClose }: ConfirmationProps) {
  return (
    <ConfirmDialog
      actionButtonText="Continue to remove pause"
      cancelButtonText="Cancel"
      cancelButtonColor="mascot"
      cancelButtonLevel="icon"
      size="small"
      onConfirm={onConfirm}
      onCancel={onClose}
      onRequestHide={onClose}>
      <Heading serif>Remove pause</Heading>
      <Text align="left" color="graphite" element="p">
        Once your remove your pause, we may start scheduling sessions based on your current
        availability schedule.
      </Text>
    </ConfirmDialog>
  );
}

type ConfirmationModalState = 'setPause' | 'cancelPause' | null;

export default function PauseTutoringView(props: Props) {
  const { onBack, onSubmit, pause, availabilityQuery } = props;
  // we use the viewing timezone for interpreting the `DateOnly` string, because
  // the javascript Date object, which the `DatePickerInput` uses, always interprets
  // dates through the lens of the viewing timezone. the `DateOnly` string will get
  // interpreted correctly on the backend using the coach's actual timezone.
  const [effectiveDate, setEffectiveDate] = useState<Date | undefined>(
    dateOrUndefined(pause?.effectiveDate)
  );
  const [returnDate, setReturnDate] = useState<Date | undefined>(
    dateOrUndefined(pause?.returnDate)
  );
  const [pauseReason, setPauseReason] = useState<PauseReason | undefined>(
    pause?.reason ? (pause.reason as PauseReason) : undefined
  );
  const [pauseDetails, setPauseDetails] = useState<string>('');
  const [unsureOfReturn, setUnsureofReturn] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalState>(null);
  const [submitting, setSubmitting] = useState(false);

  const minEffectiveDate = calcMinEffectiveDate();
  const minReturnDate = addWeeks(
    effectiveDate ?? minEffectiveDate,
    CoachingConstants.MIN_COACH_PAUSE_LENGTH_IN_WEEKS
  );

  // the `min` prop on the DatePickerInput only limits selections, not typed/pasted input,
  // so we have to ensure the return date is at least 3 weeks after the effective date here
  // in a custom onChange handler. This only updates the return date if one is already selected,
  // since we don't want to default it for the user.
  const updateReturnDate = (newReturnDate?: Date) => {
    if (!newReturnDate) return;
    if (isSameDay(minReturnDate, newReturnDate) || isAfter(newReturnDate, minReturnDate)) {
      setReturnDate(newReturnDate);
    } else setReturnDate(minReturnDate);
  };

  useEffect(() => {
    trackKey('view');
  }, []);

  useEffect(() => {
    // set the return date so it's 3 weeks from the effective date, if it's not already
    updateReturnDate(returnDate);
  }, [effectiveDate]);

  const pauseFormComplete = !!(effectiveDate && (returnDate || unsureOfReturn) && pauseReason);

  const savePause = async () => {
    if (pauseFormComplete) {
      setSubmitting(true);
      const savePausePayload = {
        uuid: pause?.overrideUuid ?? uuid.v4(),
        effectiveDate: dateOnlyFormat(effectiveDate!),
        reason: pauseReason!, // we already ensure that `pauseReason` is defined in `pauseFormComplete`
        returnDate: returnDate && !unsureOfReturn ? dateOnlyFormat(returnDate) : null,
        details: pauseDetails || null,
      };
      await saveCoachPause(savePausePayload, availabilityQuery);
      setSubmitting(false);
      onSubmit?.();
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to save a pause without an effective date, return information, or a reason. Please try again.'
      );
    }
  };

  const cancelPause = async () => {
    if (!pause) {
      console.error('Unexpected error: tried to cancel a pause that does not exist');
      return;
    }
    setSubmitting(true);
    const cancelPausePayload = { pauseUuid: pause.overrideUuid };
    await cancelCoachPause(cancelPausePayload, availabilityQuery);
    setSubmitting(false);
    onSubmit?.();
  };

  return (
    <div className="PauseTutoringView">
      <Modal data-test="pause-confirmation-modal" isOpen={!!confirmationModal}>
        {confirmationModal === 'setPause' && (
          <SetPauseConfirmation
            onClose={() => setConfirmationModal(null)}
            onConfirm={async () => {
              await savePause();
            }}
            userReadableEffectiveDate={userReadableDateFormat(effectiveDate!)}
          />
        )}
        {confirmationModal === 'cancelPause' && (
          <CancelPauseConfirmation
            onClose={() => setConfirmationModal(null)}
            onConfirm={async () => {
              await cancelPause();
            }}
          />
        )}
      </Modal>
      <Row size="xsmall" className="PauseTutoringView-back">
        <Button level="icon" color="chalkboard" onClick={onBack} data-test="back-arrow">
          <Icon name="arrow-left" />
        </Button>
      </Row>
      <Container size="small">
        <Heading level="2">Pause tutoring</Heading>
        <Text element="p" lineHeight="large">
          If you don’t want to tutor for{' '}
          <Text weight="semibold">
            {CoachingConstants.MIN_COACH_PAUSE_LENGTH_IN_WEEKS} weeks or more
          </Text>
          , you should set a pause.{'\n'}Your students will be rematched to a different tutor.
        </Text>
        <Text element="p" lineHeight="large">
          For less than {CoachingConstants.MIN_COACH_PAUSE_LENGTH_IN_WEEKS} weeks, remove
          availability for individual days.
        </Text>
        <FormElement
          data-test="pause-effective-date-input"
          label="Effective date"
          element={DatePickerInput}
          elementProps={{
            placeholder: 'Select a date',
            value: effectiveDate,
            full: true,
            min: minEffectiveDate,
            readOnly: true,
          }}
          onValidChange={(date: Date) => {
            trackKey('select_effective_date.click');
            setEffectiveDate(date);
          }}
          validate={(date: Date) => {
            assertions.notEmpty(date, 'Your pause must have an effective date.');
            assertions.atLeastOneDayInFuture(
              date,
              'Please schedule your pause at least one day in advance.'
            );
          }}
        />
        <Label>
          <Text>Return date</Text>
          <Text
            className={cx(unsureOfReturn && 'PauseTutoringView-returnDateLabel--hidden')}
            element="div"
            color="graphite"
            weight="normal">
            We'll reach out to you before this date to add availability
          </Text>
        </Label>
        <Row size="xxsmall">
          <DatePickerInput
            full
            className={cx(unsureOfReturn && 'PauseTutoringView-returnDate--hidden')}
            data-test="pause-return-date-input"
            placeholder="Select a date"
            disabled={!effectiveDate || unsureOfReturn}
            onChange={(date?: Date) => {
              trackKey('select_return_date.click');
              updateReturnDate(date);
            }}
            // we don't want to show the value of the return date
            // input until an effective date has been selected
            value={!effectiveDate || unsureOfReturn ? undefined : returnDate}
            min={minReturnDate}
          />
        </Row>
        <Row size="medium">
          <Checkbox
            data-test="unsure-of-return-checkbox"
            label={<Text weight="normal">I'm not sure</Text>}
            disabled={!effectiveDate}
            checked={unsureOfReturn}
            onChange={() => {
              trackKey('select_return_date_not_sure.click');
              setUnsureofReturn(!unsureOfReturn);
            }}
          />
        </Row>
        <CoachPauseReasonsSelect
          changeState={reason => {
            trackKey('select_reason.click');
            setPauseReason(reason);
          }}
          initialReason={pauseReason}
        />
        {pauseReason === 'other' && (
          <div>
            <FormElement
              data-test="pause-details-textarea"
              label={
                <Text>
                  Tell us more <Text weight="normal">(Optional)</Text>
                </Text>
              }
              element={Textarea}
              elementProps={{
                value: pauseDetails,
                placeholder: 'Add a message',
              }}
              onValidChange={e => {
                setPauseDetails(e.target.value);
              }}
            />
          </div>
        )}
        <Buttons align="left" margin="xxxxlarge">
          <Button
            data-test="save-pause-button"
            disabled={!pauseFormComplete}
            loading={submitting}
            onClick={async () => {
              trackKey('set_pause.click');
              setConfirmationModal('setPause');
            }}>
            {pause ? 'Update' : 'Set'} pause
          </Button>
          {!!pause && (
            <Button
              color="apple"
              level="text"
              onClick={() => setConfirmationModal('cancelPause')}
              data-test="cancel-pause-button">
              Remove pause
            </Button>
          )}
        </Buttons>
      </Container>
    </div>
  );
}
